import React from 'react';
import { AgGridColumn } from 'ag-grid-react';
import { isFeatureFlagEnabled } from '../../utils/FeatureFlag/FeatureFlagUtil';
import { PermissionsUtil } from '../../utils/permissions/PermissionsUtil';
import { PERMISSIONS } from '../../constants/permissions/permissions.constants';
import { currencyFormatter, dateFormatter } from '../../utils/formattingUtils';
import SingleSelectGrid from '../../components/Grids/SingleSelectGrid';
import DatacorLogoSpinner from '../../components/DatacorLogoSpinner/DatacorLogoSpinner';
import { Button, Card, CardHeader, CardContent } from '@mui/material';
import { numberAsStringComparator } from '../../utils/comparisonUtils';
import useDocumentListView from './hooks/useDocumentListView';
import GridOptionsPopover from '../../components/ApAutomation/GridOptionsPopover';
import FileDropper from '../../components/ApAutomation/FileDropper';
import './DocumentListView.css';

interface DocumentListViewProps {
    showChangeDefaultApproversModal: () => void;
    showChangeDefaultToleranceModal: () => void;
    showChangeApprovalWorkflows: () => void;
}

const DocumentListView = ({
    showChangeDefaultApproversModal,
    showChangeDefaultToleranceModal,
    showChangeApprovalWorkflows,
}: DocumentListViewProps) => {
    const {
        user,
        userList,
        shouldLoad,
        filteredRowData,
        onFirstDataRendered,
        onSortChanged,
        onFilterChanged,
        onGridReady,
        openDocument,
        getValuesAsync,
    } = useDocumentListView();

    return (
        <>
            <Card style={{ height: '95%' }}>
                <CardHeader
                    style={{ height: '30px' }}
                    title="Document List"
                    action={
                        <>
                            {PermissionsUtil.isPermissionEnabled(
                                user.permissions,
                                'permission.apAutomation.updateInvoice'
                            ) ? (
                                <div className="document-list-view-default-buttons">
                                    <Button
                                        data-testid="changeDefaultApprovers"
                                        onClick={
                                            showChangeDefaultApproversModal
                                        }>
                                        Default Approvers
                                    </Button>
                                    {isFeatureFlagEnabled(
                                        user.enabledFeatureFlags,
                                        'feature.apps.apAutomation.toleranceMatching'
                                    ) &&
                                    PermissionsUtil.isPermissionEnabled(
                                        user.permissions,
                                        PERMISSIONS.AP_AUTOMATION
                                            .DEFAULT_TOLERANCE.VIEW
                                    ) ? (
                                        <Button
                                            data-testid="changeDefaultTolerance"
                                            onClick={
                                                showChangeDefaultToleranceModal
                                            }>
                                            Default Tolerance
                                        </Button>
                                    ) : null}
                                    {isFeatureFlagEnabled(
                                        user.enabledFeatureFlags,
                                        'feature.apps.apAutomation.approvalWorkflows'
                                    ) &&
                                    PermissionsUtil.isPermissionEnabled(
                                        user.permissions,
                                        PERMISSIONS.AP_AUTOMATION
                                            .APPROVAL_WORKFLOWS.VIEW
                                    ) ? (
                                        <Button
                                            data-testid="changeDefaultApprovalWorkflows"
                                            onClick={
                                                showChangeApprovalWorkflows
                                            }>
                                            Approval Workflows
                                        </Button>
                                    ) : null}
                                </div>
                            ) : null}
                        </>
                    }
                />
                <CardContent style={{ height: '100%' }}>
                    {!shouldLoad ? (
                        <>
                            <div
                                style={{
                                    display: 'flex',
                                    flexDirection: 'row',
                                }}>
                                <FileDropper />
                                <GridOptionsPopover />
                            </div>

                            <SingleSelectGrid
                                testId="document-list-grid"
                                rowBuffer={0}
                                domLayout={null}
                                paginationPageSize={null}
                                pagination={false}
                                maxConcurrentDatasourceRequests={1}
                                cacheBlockSize={50}
                                cacheOverflowSize={2}
                                infiniteInitialRowCount={100}
                                maxBlocksInCache={5}
                                gridStyle={{
                                    height: '75%',
                                    width: '100%',
                                    overflow: 'scroll',
                                }}
                                immutableData={true}
                                dataKey="documentId"
                                rowModelType="infinite"
                                onFirstDataRendered={onFirstDataRendered}
                                onSortChanged={onSortChanged}
                                onFilterChanged={onFilterChanged}
                                onGridReady={onGridReady}
                                onRowClicked={openDocument}
                                rowStyle={{ cursor: 'pointer' }}
                                defaultColDef={{
                                    resizable: true,
                                    sortable: true,
                                    unSortIcon: true,
                                    filter: true,
                                    wrapText: true,
                                }}>
                                <AgGridColumn
                                    minWidth={250}
                                    field="uploader"
                                    headerName="Uploader"
                                    valueFormatter={(val) =>
                                        userList.getUsersFullNameFromEmail(
                                            val.value,
                                            true
                                        )
                                    }
                                    filter="agSetColumnFilter"
                                    filterParams={(params: any) => {
                                        const emailVals = getValuesAsync(
                                            params,
                                            'uploader'
                                        );
                                        const nameVals = emailVals.map(
                                            (email: string) => {
                                                return userList.getUsersFullNameFromEmail(
                                                    email,
                                                    true
                                                );
                                            }
                                        );
                                        return {
                                            values: nameVals,
                                        };
                                    }}
                                />
                                <AgGridColumn
                                    minWidth={150}
                                    field="invoiceNumber"
                                    headerName="Invoice Number"
                                    filter="agSetColumnFilter"
                                    filterParams={(params: any) => {
                                        return {
                                            values: getValuesAsync(
                                                params,
                                                'invoiceNumber'
                                            ),
                                        };
                                    }}
                                />
                                <AgGridColumn
                                    minWidth={125}
                                    field="supplierNumber"
                                    headerName="Supplier Number"
                                    filter="agSetColumnFilter"
                                    filterParams={(params: any) => {
                                        return {
                                            values: getValuesAsync(
                                                params,
                                                'supplierNumber'
                                            ),
                                        };
                                    }}
                                />
                                <AgGridColumn
                                    minWidth={200}
                                    field="supplierName"
                                    headerName="Supplier Name"
                                    filter="agSetColumnFilter"
                                    filterParams={(params: any) => {
                                        return {
                                            values: getValuesAsync(
                                                params,
                                                'supplierName'
                                            ),
                                        };
                                    }}
                                />
                                <AgGridColumn
                                    minWidth={150}
                                    field="due"
                                    headerName="Amount"
                                    valueFormatter={currencyFormatter}
                                    comparator={numberAsStringComparator}
                                    filter="agSetColumnFilter"
                                    filterParams={(params: any) => {
                                        const amountVals = getValuesAsync(
                                            params,
                                            'due'
                                        );
                                        const formattedDueVals = amountVals.map(
                                            (amount: string) => {
                                                return currencyFormatter(
                                                    amount
                                                );
                                            }
                                        );
                                        return {
                                            values: formattedDueVals,
                                        };
                                    }}
                                />
                                <AgGridColumn
                                    minWidth={175}
                                    field="invoiceDate"
                                    headerName="Invoice Date"
                                    filter="agSetColumnFilter"
                                    valueFormatter={dateFormatter}
                                    filterParams={(params: any) => {
                                        const dateVals = getValuesAsync(
                                            params,
                                            'invoiceDate'
                                        );
                                        const formattedDateVals = dateVals.map(
                                            (date: string) => {
                                                return dateFormatter(date);
                                            }
                                        );
                                        return {
                                            values: formattedDateVals,
                                        };
                                    }}
                                />
                                <AgGridColumn
                                    minWidth={175}
                                    field="uploadDate"
                                    headerName="Upload Date"
                                    filter="agSetColumnFilter"
                                    valueFormatter={dateFormatter}
                                    filterParams={(params: any) => {
                                        const dateVals = getValuesAsync(
                                            params,
                                            'uploadDate'
                                        );
                                        const formattedDateVals = dateVals.map(
                                            (date: string) => {
                                                return dateFormatter(date);
                                            }
                                        );
                                        return {
                                            values: formattedDateVals,
                                        };
                                    }}
                                />
                                <AgGridColumn
                                    minWidth={150}
                                    field="poNumber"
                                    headerName="PO Number"
                                    filter="agSetColumnFilter"
                                    filterParams={(params: any) => {
                                        return {
                                            values: getValuesAsync(
                                                params,
                                                'poNumber'
                                            ),
                                        };
                                    }}
                                />
                                <AgGridColumn
                                    minWidth={150}
                                    field="poStatus.value"
                                    headerName="PO Status"
                                    filter="agSetColumnFilter"
                                    filterParams={(params: any) => {
                                        return {
                                            values: getValuesAsync(
                                                params,
                                                'poStatus',
                                                'value'
                                            ),
                                        };
                                    }}
                                />
                                <AgGridColumn
                                    minWidth={175}
                                    field="approvalStatus.value"
                                    headerName="Invoice Status"
                                    filter="agSetColumnFilter"
                                    filterParams={(params: any) => {
                                        return {
                                            values: getValuesAsync(
                                                params,
                                                'approvalStatus',
                                                'value'
                                            ),
                                        };
                                    }}
                                />
                                <AgGridColumn
                                    minWidth={150}
                                    field="voucherNumber"
                                    headerName="Voucher Number"
                                    filter="agSetColumnFilter"
                                    filterParams={(params: any) => {
                                        return {
                                            values: getValuesAsync(
                                                params,
                                                'voucherNumber'
                                            ),
                                        };
                                    }}
                                />
                            </SingleSelectGrid>
                            <div
                                style={{
                                    textAlign: 'right',
                                    paddingTop: '5px',
                                }}>
                                <b>{filteredRowData?.length}</b> Invoice(s)
                            </div>
                        </>
                    ) : (
                        <DatacorLogoSpinner />
                    )}
                </CardContent>
            </Card>
        </>
    );
};

export default DocumentListView;
